import { useEffect } from 'react';
import { addDays } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import { simulationV2, exportDownloadUrlV1 } from '~/services/simulation-service';
import { useQueryCreator } from '~/hooks/useQueryCreator';
import { useStoreActions, useStoreState } from '~/store';
import { resolveSimulationServiceVersion } from '~/store/simulation/resolveSimulationServiceVersion';
import type { SimulationDetail, SimulationExportMetadata } from '~/store/simulation/types';

/** Version-blind export listing + download: v2 via React Query, v1 via the existing thunk. */
export function useSimulationExports(simulation?: SimulationDetail): {
  exports: SimulationExportMetadata[];
  loading: boolean;
  isExpired: boolean;
  download: (file: string) => Promise<string>;
} {
  const simulationId = simulation?.simulationId;
  const version = resolveSimulationServiceVersion(simulation?.simulationSetupData);
  const useV2 = version === 'v2';
  const isExpired = simulation ? addDays(new Date(simulation.creationTimestamp), 28) < new Date() : false;

  const exportsMap = useStoreState((state) => state.simulation.simulationExports);
  const storeLoading = useStoreState((state) => state.loading.loadingFlags.simulation);
  const fetchSimulationExports = useStoreActions((actions) => actions.simulation.fetchSimulationExports);
  const dispatchMessage = useStoreActions((actions) => actions.messages.dispatchMessage);

  const v2Query = useQuery({
    ...useQueryCreator(simulationV2.exportsList, { args: [simulationId ?? ''] }),
    enabled: useV2 && !!simulationId && !isExpired,
    // match the v1 thunk's failure notification
    onError: () => dispatchMessage({ noticeType: 'error', content: 'Error loading simulation exports. Please retry.' }),
  });
  useEffect(() => {
    if (!useV2 && simulationId && !isExpired) {
      fetchSimulationExports({ simulationId });
    }
  }, [useV2, simulationId, isExpired, fetchSimulationExports]);

  const download = (file: string): Promise<string> =>
    useV2 ? simulationV2.exportDownloadUrl(simulationId ?? '', file) : exportDownloadUrlV1(simulationId ?? '', file);

  return {
    exports: useV2 ? (v2Query.data ?? []) : Object.values(exportsMap ?? {}),
    loading: useV2 ? v2Query.isLoading : !!storeLoading,
    isExpired,
    download,
  };
}
